import { AppState, User } from '@auth0/auth0-react'

import { logAndCaptureMessage } from '../../utils/errorTools'
import { sessionOrFallbackStorage } from '../../utils/storage'
import { isStateMachineClaim, StateMachineClaim, userClaimIds } from '../../utils/userClaims'
import { onboardingClaims } from './config'
import { machine as patientMachine, OnboardPatientStateValue } from './state/machine-patient'
import {
  machine as practitionerMachine,
  OnboardPractitionerStateValue,
} from './state/machine-practitioner'
import { machine as studentMachine, OnboardStudentStateValue } from './state/machine-student'

/**
 * Identify whether state from Auth0 claim is valid in state machine.
 * Exclude `invalid` state (used as fallback)
 */

export const isValidPatientState = (
  state: string,
): state is Exclude<OnboardPatientStateValue, 'invalid'> => {
  return !!state && state !== 'invalid' && Object.keys(patientMachine.states).includes(state)
}

export const isValidPractitionerState = (
  state: string,
): state is Exclude<OnboardPractitionerStateValue, 'invalid'> => {
  return !!state && state !== 'invalid' && Object.keys(practitionerMachine.states).includes(state)
}

export const isValidStudentState = (
  state: string,
): state is Exclude<OnboardStudentStateValue, 'invalid'> => {
  return !!state && state !== 'invalid' && Object.keys(studentMachine.states).includes(state)
}

/*
  Functions and types to handle Practitioner Form State
  Ref: https://app.shortcut.com/designsforhealth/story/54970/discuss-implement-we-need-to-add-a-toast-message-banner-to-inform-existing-practitioners-they-need-to-upload-their-forms
*/

export type PractitionerFormActionTypes =
  | { type: 'prac_form_initialized' }
  | { type: 'toast_dismissed' }
  | { type: 'toast_selected' }
  | { type: 'commissions_pref_selected' }
  | { type: 'remind_me_later_selected' }
  | { type: 'onboarding_complete' }

type PractitionerFormAction = PractitionerFormActionTypes['type']

export interface PractitionerFormsState {
  lastEvent: PractitionerFormAction
  notifyAfter: string
}

export const isPractitionerFormsState = (formState: unknown): formState is PractitionerFormsState =>
  formState != null &&
  typeof formState === 'object' &&
  typeof (formState as Record<string, unknown>).lastEvent === 'string' &&
  typeof (formState as Record<string, unknown>).notifyAfter === 'string'

export type ActiveOnboarding = {
  id: (typeof onboardingClaims)[0]['claimId']
  claim: StateMachineClaim
}

export const getActiveOnboarding = (user?: User): ActiveOnboarding | null => {
  if (!user) return null

  const found = onboardingClaims.find(({ claimId, validateState }) => {
    const claim = user[claimId]
    if (!isStateMachineClaim(claim) || !claim.state) {
      return false
    }
    const state = claim.state.trim()
    if (!validateState(state)) {
      logAndCaptureMessage(`Invalid ${claimId} state "${state}" in user claim`, {
        level: 'warning',
      })
      return false
    }
    return true
  })
  if (!found) return null
  const customerId = user[userClaimIds.netSuiteId]
  const claim = user[found.claimId]
  return {
    id: found.claimId,
    claim: {
      // if the customerId is present, there's no need to continue onboarding
      state: customerId ? 'complete' : claim.state.trim(),
      context: claim.context ?? {},
    },
  }
}

export const BEFORE_ONBOARDING_STATE = 'beforeOnboardingState'

export const saveBeforeOnboardingState = (state: AppState) => {
  try {
    sessionOrFallbackStorage.setItem(BEFORE_ONBOARDING_STATE, JSON.stringify(state))
  } catch (_) {
    // Ignore storage errors; not critical
  }
}
